/**
 * Fetch 拦截器
 * 重写全局 fetch 函数，添加请求和响应拦截器
 */

import { appApi } from '../services/loginApi';
import { useAuthStore } from '../stores/auth'
import { showNotify, showLoadingToast, closeToast } from 'vant'
import router from '../router/index';
// 保存原始的 fetch 函数
const originalFetch = window.fetch;

// 请求拦截器类型
type RequestInterceptor = (url: string | URL | Request, config?: RequestInit) => Promise<{ url: string | URL | Request, config?: RequestInit }>;

// 响应拦截器类型
type ResponseInterceptor = (response: Response) => Promise<Response>;

// 错误拦截器类型
type ErrorInterceptor = (error: any) => Promise<any>;

class FetchInterceptor {
  private static requestInterceptors: RequestInterceptor[] = [];
  private static responseInterceptors: ResponseInterceptor[] = [];
  private static errorInterceptors: ErrorInterceptor[] = [];

  /**
   * 添加请求拦截器
   */
  static addRequestInterceptor(interceptor: RequestInterceptor) {
    this.requestInterceptors.push(interceptor);
  }

  /**
   * 添加响应拦截器
   */
  static addResponseInterceptor(interceptor: ResponseInterceptor) {
    this.responseInterceptors.push(interceptor);
  }

  /**
   * 添加错误拦截器
   */
  static addErrorInterceptor(interceptor: ErrorInterceptor) {
    this.errorInterceptors.push(interceptor);
  }

  /**
   * 执行请求拦截器
   */
  private static async runRequestInterceptors(url: string | URL | Request, config?: RequestInit) {
    let finalUrl = url;
    let finalConfig = config;

    for (const interceptor of this.requestInterceptors) {
      const result = await interceptor(finalUrl, finalConfig);
      finalUrl = result.url;
      finalConfig = result.config;
    }

    return { url: finalUrl, config: finalConfig };
  }

  /**
   * 执行响应拦截器
   */
  private static async runResponseInterceptors(response: Response): Promise<Response> {
    let finalResponse = response;

    for (const interceptor of this.responseInterceptors) {
      finalResponse = await interceptor(finalResponse);
    }

    return finalResponse;
  }

  /**
   * 执行错误拦截器
   */
  private static async runErrorInterceptors(error: any): Promise<any> {
    let finalError = error;

    for (const interceptor of this.errorInterceptors) {
      finalError = await interceptor(finalError);
    }

    return finalError;
  }

  /**
   * 初始化拦截器，重写全局 fetch
   */
  static init() {
    window.fetch = async (url: string | URL | Request, config?: RequestInit & { showLoading?: boolean; loadingText?: string, noError?: string, responseType?: 'json' | 'blob' | 'arraybuffer' }): Promise<Response> => {
      const currentShowLoading = config?.showLoading ?? false;
      const currentLoadingText = config?.loadingText ?? '';
      try {
        console.log('🚀 Fetch 拦截器执行:', url);

        // 执行请求拦截器
        const { url: interceptedUrl, config: interceptedConfig } = await this.runRequestInterceptors(url, config);

        if (currentShowLoading) {
          this.showGlobalLoading(currentLoadingText);
        }
        // 调用原始 fetch
        const response = await originalFetch(interceptedUrl, interceptedConfig);
        (response as any)._responseType = config?.responseType || 'json';
        (response as any)._noError = config?.noError;
        // 执行响应拦截器
        const interceptedResponse = await this.runResponseInterceptors(response);

        console.log('✅ Fetch 响应成功:', interceptedResponse.url, interceptedResponse.status);

        return interceptedResponse;
      } catch (error) {
        console.error('❌ Fetch 请求失败:', error);
        
        // 执行错误拦截器
        const handledError = await this.runErrorInterceptors(error);
        throw handledError;
      } finally {
        // 5. 无论成功/失败，都关闭 loading（核心：请求结束后调用）
        if (currentShowLoading) {
          this.hideGlobalLoading();
        }
      }
    }

    // 添加默认的请求拦截器
    this.addRequestInterceptor(async (url, config) => {
      const finalConfig = { ...config };
      // 获取 accessToken
      try {
        const accessToken = appApi.getValidAccessToken();
        if (accessToken) {
          finalConfig.headers = {
            ...finalConfig.headers,
            'ACCESS-TOKEN': accessToken
          };
        }
      } catch (error) {
      }
      // 添加默认 Content-Type
      if (!finalConfig.headers) {
        finalConfig.headers = {};
      }
      if (!(finalConfig.headers as any)['Content-Type'] && finalConfig.method !== 'GET' && !(finalConfig as any)['customType']) {
        (finalConfig.headers as any)['Content-Type'] = 'application/json';
      }
      const piniaUserInfo = useAuthStore().getUserInfo() as UserInfo | null | undefined;
      interface UserInfo {
        openid: string;
        unionid: string;
        userPhone: string;
        // 可添加其他用户信息字段（如 username、avatar 等），按需扩展
      }
      let sessionUserInfo:UserInfo = {
        openid: '',
        unionid: '',
        userPhone: ''
      };
      try {
        const sessionUserInfoStr = localStorage.getItem('userInfo');
        if (sessionUserInfoStr) {
          const parsed = JSON.parse(sessionUserInfoStr) as Partial<UserInfo>;
          sessionUserInfo = {
            openid: parsed.openid || '',
            unionid: parsed.unionid || '',
            userPhone: parsed.userPhone || ''
          };
        }
      } catch (error) {
        sessionUserInfo = { openid: '', unionid: '', userPhone: '' };
      }
      const openid = piniaUserInfo?.openid ?? sessionUserInfo.openid;
      const unionid = piniaUserInfo?.unionid ?? sessionUserInfo.unionid;
      const userPhone = piniaUserInfo?.userPhone ?? sessionUserInfo.userPhone;
      const method = (finalConfig.method || 'GET').toUpperCase(); // 默认 GET 方法
      if (typeof url === 'string') {
        if (method === 'GET') {
          // GET 请求：拼接到 URL 参数中
          const [baseUrl, existingParams] = url.split('?');
          const searchParams = new URLSearchParams(existingParams || '');
          const configWithParams = config as any;
          if (configWithParams?.params && typeof configWithParams.params === 'object') {
            Object.keys(configWithParams.params).forEach(key => {
              const value = configWithParams.params[key];
              if (value !== undefined && value !== null) {
                searchParams.set(key, String(value));
              }
            });
            // 删除 params 属性，避免传递给原始 fetch
            delete (finalConfig as any).params;
          }
          // searchParams.set('openid', openid);
          // searchParams.set('unionid', unionid);
          // searchParams.set('userPhone', userPhone);
          url = `${baseUrl}?${searchParams.toString()}`;
        } else if (method === 'POST') {
          const customType = (finalConfig as any)['customType'];
          if (customType === 'fileUpload') {
            return { url, config: finalConfig }; // 跳过后续处理
          }
          // POST 请求：添加到请求体中（兼容 JSON 格式和表单格式）
          const contentType = (finalConfig.headers as any)['Content-Type'];
          if (contentType === 'application/json') {
            // JSON 格式：解析 body 后添加 openid
            let body = finalConfig.body ? JSON.parse(finalConfig.body as string) : {};
            body = {
              ...body,
              // openid, // 添加 openid
              // unionid, // 添加 unionid
              // userPhone // 添加 userPhone
            };
            finalConfig.body = JSON.stringify(body);
          } else if (contentType === 'application/x-www-form-urlencoded') {
            const formData = finalConfig.body ? new URLSearchParams(finalConfig.body as string) : new URLSearchParams();
            // formData.set('openid', openid);
            // formData.set('unionid', unionid);
            // formData.set('userPhone', userPhone);
            finalConfig.body = formData.toString();
          }
        }
      }
      return { url, config: finalConfig };
    });

    // 添加默认的响应拦截器
    this.addResponseInterceptor(async (response) => {
      const responseType = (response as any)._responseType || 'json';
      const noError = (response as any)._noError;
      // 获取响应头中的 accessToken 并存储
      const accessToken = response.headers.get('access-token');
      if (accessToken) {
        try {
          console.log('✅ accessToken 已从 fetch 响应头获取并存储');
        } catch (error) {
          console.error('❌ 存储 accessToken 失败:', error);
        }
      }
      if (!response.ok) {
        if (response.status === 401) {
          // 清除本地 token
          appApi.clearStoredToken()
          // 显示登录提示
          showNotify({
            type: 'warning',
            message: '请先注册/登录后再操作',
            duration: 3000
          });
          // 跳转到登录页
          if (router) {
            setTimeout(() => {
              const currentRoute = router.currentRoute.value;
              if (!currentRoute || currentRoute.path !== '/user') {
                router.push('/user').catch(err => {
                  console.error('401跳转登录页失败:', err);
                });
              }
            }, 1500);
          } else {
            console.error('路由实例未找到，无法跳转到登录页');
          }
          // 抛出401错误
          return Promise.reject({
            error: 'Unauthorized',
            status: 401,
            response
          });
        }
        showNotify({
          type: 'danger',
          message: '请求失败，请稍后重试', // 兜底提示
          duration: 3000
        })
        return Promise.reject({
          error: 'response is not ok',
          response
        })
      }
      // 获取请求配置中的 responseType（关键：判断是否是文件下载）
      if (responseType === 'blob' || responseType === 'arraybuffer') {
        return response; // 不解析 JSON，直接返回给上层处理文件下载
      }
      // 只有非文件下载的请求，才执行 JSON 解析
      const responseClone = response.clone(); // 避免原始响应流被消耗
      let data: any;
      try {
        data = await responseClone.json();
      } catch (error) {
        showNotify({
          type: 'danger',
          message: '请求失败，请稍后重试',
          duration: 3000
        })
        return Promise.reject({
          error: 'JSON parse failed',
          response
        });
      }
      if (typeof data === 'object' && data !== null && 'success' in data && !data.success) {
        if (!noError) { // 存储了当前请求的原始配置参数，如果没有传递noError 则提示接口返回的error
          showNotify({
            type: 'danger',
            message: data.error || '请求失败，请稍后重试', // 兜底提示
            duration: 3000
          })
        } else {
          showNotify({
            type: 'danger',
            message: noError || '请求失败，请稍后重试', // 兜底提示
            duration: 3000
          })
        }
        // 抛出错误，让上层catch捕获
        return Promise.reject(data);
      }
      return response;
    });

    // 添加默认的错误拦截器
    this.addErrorInterceptor(async (error) => {
      // 处理不同类型的错误
      if (error.name === 'AbortError') {
        console.error('⏰ Fetch 请求超时');
        error.message = '请求超时，请检查网络连接';
      } else if (!navigator.onLine) {
        console.error('📶 网络连接失败');
        error.message = '网络连接失败，请检查网络设置';
      }

      return Promise.reject(error);
    });

    console.log('✅ Fetch 拦截器已初始化');
  }

  /**
   * 恢复原始的 fetch 函数
   */
  static restore() {
    window.fetch = originalFetch;
    console.log('✅ Fetch 拦截器已恢复原始状态');
  }
  private static loadingCount = 0; // 并发请求计数器
  private static isLoadingShow = false; // 标记是否已显示 Loading

  /**
   * 显示 loading（使用 Vant 4.x showLoadingToast）
   */
  private static showGlobalLoading(loadingText = '') {
    if (this.loadingCount === 0 && !this.isLoadingShow) {
      // 调用 Vant 4.x 原生 API，无需创建实例
      showLoadingToast({
        message: loadingText,
        type: 'loading',
        forbidClick: true, // 禁止点击背景
        loadingType: 'circular', // 图标类型（circle/spinner 二选一）
        overlay: true, // 显示半透明背景（增强体验，适配小程序）
        overlayStyle: {
          background: 'rgba(0, 0, 0, 0.1)' // 自定义遮罩样式（0.1 为透明度，范围 0-1）
        }, 
        className: loadingText ? '' : 'custom-loading-empty',
        zIndex: 11112, // 确保在小程序 web-view 最上层
        duration: 0, // 0 表示不自动关闭（必须手动调用 closeToast）
      });
      this.isLoadingShow = true;
    }
    this.loadingCount++;
  }

  /**
   * 关闭 loading（使用 Vant 4.x closeToast）
   */
  private static hideGlobalLoading() {
    this.loadingCount--;
    if (this.loadingCount < 0) this.loadingCount = 0;

    if (this.loadingCount === 0 && this.isLoadingShow) {
      closeToast(); // 直接关闭 Loading，Vant 4.x 自动处理
      this.isLoadingShow = false;
    }
  }
}

export default FetchInterceptor;
